import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { syncTimezoneToBackend } from '../utils/timeAgo';
import { purgeAllRecentlyViewed, sweepLegacyRecentlyViewedKeys } from '../utils/recentlyViewed';
import { purgeAllHiddenListings, sweepLegacyHiddenListingKeys } from '../utils/hiddenListings';
import { purgeAllRecentCategories, sweepLegacyRecentCategoryKeys } from '../utils/forYou';
import { purgeAllRecentSearches, sweepLegacyRecentSearchKeys } from '../lib/recentSearches';
import { purgeAllAdFormDefaults, sweepLegacyAdFormDefaultKeys } from '../utils/adFormDefaults';
import { getApiBase } from '../api/httpClient';
import fetchWithTimeout from '../utils/fetchWithTimeout';
import { toast } from 'sonner';
import { registerNativeAuthListener, exchangeSessionId, startNativeGoogleLogin, isNativeApp, isOAuthReturnPending, setOAuthPending, getLaunchSessionId, registerBrowserFinishedListener } from '../utils/nativeGoogleAuth';

const AuthContext = createContext();

// Onboarding version constants
const ONBOARDING_KEY = 'onboarding_version_completed';
const ONBOARDING_DONE = 'v2';

// Check if v2 onboarding is complete (local fast check)
function isOnboardingV2Complete() {
  return localStorage.getItem(ONBOARDING_KEY) === ONBOARDING_DONE;
}

// Mark v2 onboarding as complete
function markOnboardingV2Complete() {
  localStorage.setItem(ONBOARDING_KEY, ONBOARDING_DONE);
}

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(() => {
    // Sanitize legacy junk values that some older builds wrote (e.g. the
    // literal strings "null"/"undefined" from JSON.stringify(null)). If we
    // hand those back, ProtectedRoute mistakes them for a valid token and
    // gets stuck on the loading spinner forever.
    try {
      const t = localStorage.getItem('token');
      if (!t || t === 'null' || t === 'undefined') {
        if (t) localStorage.removeItem('token');
        return null;
      }
      return t;
    } catch (_e) {
      return null;
    }
  });
  const [loading, setLoading] = useState(true);
  const [authError, setAuthError] = useState(false);
  const [isReady, setIsReady] = useState(false); // New: track when auth is fully ready
  const [showOnboarding, setShowOnboarding] = useState(false); // OLD role selection (legacy)
  const [showCoinsPopup, setShowCoinsPopup] = useState(false);
  const [needsProfileType, setNeedsProfileType] = useState(false); // NEW profile type picker
  // Native Google sign-in: full-screen "Signing you in…" state. Initialized
  // from the persisted pending flag so a COLD-START return through
  // wassla://oauth renders the overlay on the very first frame — the
  // welcome/login page is never visible during an active OAuth callback.
  const [oauthInProgress, setOauthInProgress] = useState(() => isOAuthReturnPending());
  
  // Use ref to track if initial auth check is complete
  const initialCheckDone = useRef(false);
  // Use ref to track if a fetch is in progress to prevent race conditions
  const fetchInProgress = useRef(false);

  // Memoized fetchUser to prevent unnecessary re-renders
  const fetchUser = useCallback(async (authToken) => {
    // Prevent concurrent fetches
    if (fetchInProgress.current) {
      console.log('[Auth] Fetch already in progress, skipping...');
      return;
    }
    
    if (!authToken) {
      console.log('[Auth] No token provided, skipping fetch');
      setLoading(false);
      setIsReady(true);
      initialCheckDone.current = true;
      return;
    }

    fetchInProgress.current = true;
    console.log('[Auth] Fetching user with token...');
    setAuthError(false);
    
    try {
      // iter285 — use getApiBase() so live custom domains hit
      // same-origin /api/auth/me. Avoids cross-origin failures when the
      // build-time REACT_APP_BACKEND_URL doesn't match the live host.
      const response = await fetchWithTimeout(`${getApiBase()}/api/auth/me`, {
        headers: {
          'Authorization': `Bearer ${authToken}`
        },
        credentials: 'include'
      });

      if (response.ok) {
        const userData = await response.json();
        console.log('[Auth] User fetched. accountType:', userData?.account_type || userData?.accountType, 'onboardingCompleted:', userData?.onboarding_completed || userData?.onboardingCompleted);
        setUser(userData);
        
        // SIMPLIFIED ONBOARDING CHECK
        // Show profile picker only when the user hasn't completed
        // onboarding yet AND hasn't picked an account_type. Once either is
        // true on the user document the picker is suppressed across every
        // device + session (backend is source of truth).
        const hasAccountType = !!(userData.account_type || userData.accountType || userData.profile_type);
        const onboardingComplete = !!(userData.onboarding_completed || userData.onboardingCompleted);
        setNeedsProfileType(!hasAccountType && !onboardingComplete);
        setShowOnboarding(false);

        // Mark as complete if user has onboarding done
        if (onboardingComplete || hasAccountType) {
          markOnboardingV2Complete();
        }
        
        // Sync profile type to localStorage so ProfileTypePicker never re-shows
        const profileType = userData.account_type || userData.accountType || userData.profile_type;
        if (profileType) {
          localStorage.setItem('wassla_profile_type', profileType);
          localStorage.setItem('hasCompletedProfileSelection', 'true');
        }
      } else {
        console.log('[Auth] Token invalid, response status:', response.status);
        // iter379 — 404 means the token's signature verified but the user no
        // longer exists in THIS backend's DB (e.g. a token issued by another
        // environment sharing the JWT secret). It is just as terminal as
        // 401/403: keep it and every protected page dead-spins forever.
        if (response.status === 401 || response.status === 403 || response.status === 404) {
          localStorage.removeItem('token');
          setToken(null);
          setUser(null);
        } else {
          // 5xx / unexpected status — keep the token (may be transient) but
          // surface a visible retry state instead of an endless spinner.
          setAuthError(true);
        }
      }
    } catch (error) {
      console.error('[Auth] Failed to fetch user:', error);
      // Network errors should NOT logout the user - they might just be temporary
      // Keep the token and user state as-is, but surface a retry state.
      setAuthError(true);
    } finally {
      setLoading(false);
      setIsReady(true);
      initialCheckDone.current = true;
      fetchInProgress.current = false;
    }
  }, []);

  // Initial auth check on mount - only runs once
  useEffect(() => {
    // Boot-time sweep: remove any pre-v2 per-user-scoped localStorage
    // buckets written by older unsafe code (Recently Viewed, Hidden
    // Listings, Recent Categories, Recent Searches, Student/Ladies
    // search history). Safe even for brand-new browsers (no-op). This
    // guarantees a fresh session can never inherit state from a previous
    // user on the same device.
    sweepLegacyRecentlyViewedKeys();
    sweepLegacyHiddenListingKeys();
    sweepLegacyRecentCategoryKeys();
    sweepLegacyRecentSearchKeys();
    sweepLegacyAdFormDefaultKeys();
    // Student Hub V1 + Ladies Hub legacy search-history shared buckets.
    // These are unscoped keys that used to leak across users; v2
    // components never write them but we sweep them in case they were
    // seeded by older builds.
    try {
      localStorage.removeItem('wassla.studentHub.searchHistory.v1');
      localStorage.removeItem('ladies_search_history_v1');
    } catch { /* ignore */ }

    const storedToken = localStorage.getItem('token');
    console.log('[Auth] Initial mount, token exists:', !!storedToken);
    
    if (storedToken) {
      fetchUser(storedToken);
    } else {
      setLoading(false);
      setIsReady(true);
      initialCheckDone.current = true;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Empty dependency array - only run on mount

  const login = useCallback((newToken, userData, isNewUser = false, needsOnboarding = null) => {
    console.log('[Auth] Login called. accountType:', userData?.account_type || userData?.accountType, 'onboardingCompleted:', userData?.onboarding_completed || userData?.onboardingCompleted);

    // Hardening: sweep legacy (pre-v2) buckets on every login too, in
    // case someone signs in without going through logout (shared
    // device, manual token injection, SSO callbacks).
    sweepLegacyRecentlyViewedKeys();
    sweepLegacyHiddenListingKeys();
    sweepLegacyRecentCategoryKeys();
    sweepLegacyRecentSearchKeys();
    sweepLegacyAdFormDefaultKeys();
    try {
      localStorage.removeItem('wassla.studentHub.searchHistory.v1');
      localStorage.removeItem('ladies_search_history_v1');
      // Session-scoped picker dismissal belongs to the PREVIOUS user in this
      // tab — clear it so a different (new) account still gets its once-only
      // onboarding. Server-side flags remain the real gate.
      sessionStorage.removeItem('wassla_profile_picker_dismissed_session');
    } catch { /* ignore */ }

    localStorage.setItem('token', newToken);
    setToken(newToken);
    setUser(userData);
    setLoading(false);
    setIsReady(true);
    
    // Show the first-time picker ONLY when the account has neither a profile
    // type nor the server-side onboarding_completed flag. The server flag is
    // the source of truth: completed/dismissed/seen once = never shown again
    // on any device, reinstall or re-login.
    const hasAccountType = !!(userData?.account_type || userData?.accountType || userData?.profile_type);
    const onboardingComplete = !!(userData?.onboarding_completed || userData?.onboardingCompleted);
    setNeedsProfileType(!hasAccountType && !onboardingComplete);
    setShowOnboarding(false);
    
    // Mark onboarding complete if user already has it done
    if (onboardingComplete || hasAccountType) {
      markOnboardingV2Complete();
    }
    
    // Sync profile type to localStorage so ProfileTypePicker never re-shows
    const profileType = userData?.account_type || userData?.accountType || userData?.profile_type;
    if (profileType) {
      localStorage.setItem('wassla_profile_type', profileType);
      localStorage.setItem('hasCompletedProfileSelection', 'true');
    }
    
    // Sync user's timezone to backend (once per session)
    syncTimezoneToBackend(newToken, process.env.REACT_APP_BACKEND_URL);
    
    // Show coins popup for new users
    if (isNewUser) {
      setShowCoinsPopup(true);
    }
  }, []);

  // Native Android: complete Google sign-in when the app is re-opened via the
  // wassla://oauth deep link carrying a one-time session_id from the bridge page.
  // ---- Native Google OAuth orchestration (race-free, no timers) ----
  // exchangingRef is set SYNCHRONOUSLY when a session_id arrives, before any
  // await — the browserFinished cancel-detector reads it to distinguish a
  // successful return from a user-cancelled Custom Tab.
  const exchangingRef = useRef(false);
  const processedSessionsRef = useRef(new Set());
  // Set on SUCCESSFUL exchange, BEFORE login() commits. While true the
  // overlay must stay mounted — it is only cleared by completeOAuthTransition
  // once the router has actually committed the authenticated destination
  // (Home) with the user present. This closes the final one-frame race where
  // clearing in `finally` landed in the same React commit as login() while
  // HashRouter was still on /welcome (hashchange fires one task later).
  const oauthSuccessRef = useRef(false);

  const clearOAuthState = useCallback(() => {
    oauthSuccessRef.current = false;
    setOAuthPending(false);
    setOauthInProgress(false);
  }, []);

  const handleOAuthSession = useCallback(async (sessionId) => {
    if (processedSessionsRef.current.has(sessionId)) return; // dedupe cold-start double-fire
    processedSessionsRef.current.add(sessionId);
    exchangingRef.current = true;
    setOauthInProgress(true);
    try {
      const data = await exchangeSessionId(sessionId);
      if (data?.token && data?.user) {
        // Hold the overlay through login + navigation. Cleared only by the
        // AppRouter commit-signal effect (completeOAuthTransition) after Home
        // has committed — zero intermediate Welcome/Login frames.
        oauthSuccessRef.current = true;
        login(data.token, data.user, data.is_new_user, data.needs_onboarding);
        window.location.hash = '#/';
      } else {
        toast.error('Google sign-in failed. Please try again.');
      }
    } catch (e) {
      const isRtl = document.documentElement.dir === 'rtl';
      if (e?.status === 401) {
        toast.error(isRtl
          ? 'انتهت صلاحية جلسة تسجيل الدخول. حاول مرة أخرى.'
          : 'Sign-in session expired. Please try again.');
      } else {
        toast.error(isRtl
          ? 'تعذّر إكمال تسجيل الدخول عبر Google. تحقق من اتصالك.'
          : 'Could not complete Google sign-in. Check your connection.');
      }
    } finally {
      exchangingRef.current = false;
      // Failure/cancel paths clear immediately; the success path keeps the
      // overlay until the authenticated route commit (see above).
      if (!oauthSuccessRef.current) clearOAuthState();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [login]);

  // Called by AppRouter AFTER the router has committed the authenticated
  // destination ('/') with the user present — the only point where clearing
  // the overlay can no longer expose Welcome/Login.
  const completeOAuthTransition = useCallback(() => {
    clearOAuthState();
  }, [clearOAuthState]);
  const handleOAuthSessionRef = useRef(handleOAuthSession);
  handleOAuthSessionRef.current = handleOAuthSession;

  // Called at the "Continue with Google" tap — establishes the signing-in
  // state BEFORE the system browser opens, so the resumed WebView's first
  // visible frame is always the overlay, never the welcome page.
  const beginGoogleOAuth = useCallback(async () => {
    setOAuthPending(true);
    setOauthInProgress(true);
    try {
      await startNativeGoogleLogin();
    } catch (e) {
      clearOAuthState();
      throw e;
    }
  }, [clearOAuthState]);

  useEffect(() => {
    const unregisterDeepLink = registerNativeAuthListener((sid) => handleOAuthSessionRef.current(sid));
    // User closed the Custom Tab without completing sign-in → deterministic
    // cancel signal (the success path sets exchangingRef before this fires).
    const unregisterBrowser = registerBrowserFinishedListener(() => {
      // A late browserFinished after a SUCCESSFUL exchange must not clear the
      // overlay early (that would reopen the one-frame Welcome race).
      if (!exchangingRef.current && !oauthSuccessRef.current) clearOAuthState();
    });
    (async () => {
      if (!isNativeApp()) {
        // Web never uses the pending flag — clear any stale value defensively.
        if (isOAuthReturnPending() && !exchangingRef.current) clearOAuthState();
        return;
      }
      const launchSid = await getLaunchSessionId();
      if (launchSid) {
        handleOAuthSessionRef.current(launchSid);
      } else if (isOAuthReturnPending() && !exchangingRef.current) {
        // App launched normally while a stale pending flag exists (e.g. the
        // user abandoned the browser and relaunched later) → clear it.
        clearOAuthState();
      }
    })();
    return () => { unregisterDeepLink(); unregisterBrowser(); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // DEV-ONLY E2E harness — drives the exact OAuth success/cancel code path so
  // the transition can be frame-recorded in preview (no native Custom Tab
  // there). NODE_ENV check ⇒ dead-code-eliminated from production/AAB builds.
  useEffect(() => {
    if (process.env.NODE_ENV === 'production') return undefined;
    window.__wasslaOAuthTest = {
      begin: () => { setOAuthPending(true); setOauthInProgress(true); },
      succeed: (tk, usr) => {
        oauthSuccessRef.current = true;
        login(tk, usr, false, null);
        window.location.hash = '#/';
      },
      cancel: () => clearOAuthState(),
      state: () => ({ pending: isOAuthReturnPending(), success: oauthSuccessRef.current }),
    };
    return () => { delete window.__wasslaOAuthTest; };
  }, [login, clearOAuthState]);

  const logout = useCallback(async () => {
    console.log('[Auth] Logout called');
    // Capture the token, then clear ALL local auth state SYNCHRONOUSLY before
    // any network await. Callers do `logout(); navigate('/welcome')` without
    // awaiting — if `user` were still set when Welcome mounts, its
    // authenticated-redirect guard would bounce the user to Home instead.
    const currentToken = localStorage.getItem('token');
    localStorage.removeItem('token');
    
    // Clear all user-specific cached data to prevent leakage to next user
    try {
      sessionStorage.removeItem('studentHub.level');
      sessionStorage.removeItem('studentHub.books.genre');
      localStorage.removeItem('studentHub.level');
      localStorage.removeItem('studentHub.books.genre');
      localStorage.removeItem('studentHub.lastUserId');
      // Clear legacy unscoped keys that may contain previous user data
      localStorage.removeItem('favorites');
      localStorage.removeItem('notificationPrefs');
      // Per-user persistent state — nuke every bucket (shared legacy +
      // v1 + v2 per-user) for Recently Viewed, Hidden Listings, Recent
      // Categories and Recent Searches so no history from any prior
      // user can be read by the next session. Also wipe per-user
      // Student/Ladies search-history v2 buckets.
      purgeAllRecentlyViewed();
      purgeAllHiddenListings();
      purgeAllRecentCategories();
      purgeAllRecentSearches();
      purgeAllAdFormDefaults();
      try {
        for (let i = localStorage.length - 1; i >= 0; i--) {
          const k = localStorage.key(i);
          if (!k) continue;
          if (
            k === 'wassla.studentHub.searchHistory.v1' ||
            k.startsWith('wassla.studentHub.searchHistory.v2.') ||
            k === 'ladies_search_history_v1' ||
            k.startsWith('ladies_search_history_v2_')
          ) {
            localStorage.removeItem(k);
          }
        }
      } catch { /* ignore */ }
      localStorage.removeItem('wassla_recent_searches_v1');
      // Clear Student Hub search histories (not user-scoped)
      localStorage.removeItem('wassla.studentHub.searchHistory.v1');
      // Clear category-specific student search histories
      const keysToRemove = [];
      for (let i = 0; i < localStorage.length; i++) {
        const k = localStorage.key(i);
        if (k && k.startsWith('wassla.student.') && k.endsWith('.searchHistory.v1')) {
          keysToRemove.push(k);
        }
      }
      keysToRemove.forEach(k => localStorage.removeItem(k));
      // Clear profile picker session state
      sessionStorage.removeItem('wassla_profile_picker_dismissed_session');
      // Clear page view state from session
      sessionStorage.removeItem('pvsState_home');
      sessionStorage.removeItem('pvsState_shop');
      sessionStorage.removeItem('buyerLocation');
    } catch {}
    
    setToken(null);
    setUser(null);
    setShowOnboarding(false);
    setNeedsProfileType(false);
    setIsReady(true);
    // Don't set loading to true on logout - we know the state

    // Best-effort server-side logout AFTER local state is already cleared.
    if (currentToken) {
      try {
        await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/auth/logout`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${currentToken}`
          },
          credentials: 'include'
        });
      } catch (error) {
        console.error('[Auth] Logout error:', error);
      }
    }
  }, []);

  const updateUser = useCallback((updates) => {
    if (!updates) return;
    setUser(currentUser => {
      if (!currentUser) return currentUser;
      // Add cache busting to avatar if it's being updated
      if (updates.avatar && typeof updates.avatar === 'string' && updates.avatar.startsWith('/uploads/')) {
        updates.avatar = `${updates.avatar}?v=${Date.now()}`;
      }
      return { ...currentUser, ...updates };
    });
  }, []);

  const updateCoins = useCallback((newCoins) => {
    setUser(currentUser => {
      if (!currentUser) return currentUser;
      return { ...currentUser, coins: newCoins };
    });
  }, []);

  // Refresh user data (e.g., after profile update)
  const refreshUser = useCallback(async () => {
    const currentToken = localStorage.getItem('token');
    if (currentToken) {
      await fetchUser(currentToken);
    }
  }, [fetchUser]);

  // iter379 — explicit retry for the ProtectedRoute error screen. Re-enters
  // the loading state so the UI shows a spinner while the request is live,
  // then lands in success / login redirect / error+retry. Never a dead end.
  const retryAuth = useCallback(() => {
    const currentToken = localStorage.getItem('token');
    if (!currentToken) {
      setAuthError(false);
      setToken(null);
      return;
    }
    setLoading(true);
    setAuthError(false);
    fetchUser(currentToken);
  }, [fetchUser]);

  // Set role and close onboarding
  const setRole = useCallback(async (role) => {
    console.log('[Auth] Setting role:', role);
    const currentToken = localStorage.getItem('token');
    if (!currentToken) {
      console.error('[Auth] No token available for setting role');
      return false;
    }
    
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/users/me/role`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${currentToken}`
        },
        body: JSON.stringify({ role })
      });
      
      if (response.ok) {
        const data = await response.json();
        console.log('[Auth] Role set successfully:', data);
        // Update user with new role
        setUser(currentUser => currentUser ? { ...currentUser, role, needs_onboarding: false } : currentUser);
        setShowOnboarding(false);
        return true;
      } else {
        console.error('[Auth] Failed to set role:', response.status);
        return false;
      }
    } catch (error) {
      console.error('[Auth] Error setting role:', error);
      return false;
    }
  }, []);

  // ────────────────────────────────────────────────────────────────────────
  // Profile type / role — SINGLE SOURCE OF TRUTH
  //
  // Historically the app had two independent flows that each wrote to a
  // different backend field (`role` via /api/auth/role, `profile_type` via
  // /api/me/profile-type) and only updated their half of the user object.
  // Account.js displays `user.role || user.account_type || user.profile_type`,
  // so whichever field was touched last won — leading to the Settings-drawer
  // flow not refreshing the badge because `user.role` still held the old value.
  //
  // `setProfileType` is now the ONE helper for every entry point. It:
  //   1. Optimistically updates user.role, user.account_type and
  //      user.profile_type in local state so the whole UI re-renders together.
  //   2. Persists to BOTH backend endpoints in parallel, keeping the two
  //      fields in sync server-side.
  //   3. Refreshes localStorage caches so reload/relogin stays consistent.
  // All legacy names (`setRole`, callers of /api/auth/role) should delegate
  // here — do NOT write user.role in isolation again.
  // ────────────────────────────────────────────────────────────────────────
  const setProfileType = useCallback(async (profileType) => {
    console.log('[Auth] Setting profile type (unified):', profileType);
    const currentToken = localStorage.getItem('token');
    if (!currentToken) {
      console.error('[Auth] No token available for setting profile type');
      return false;
    }

    // Mark v2 onboarding complete immediately to prevent legacy onboarding
    markOnboardingV2Complete();
    localStorage.setItem('wassla_profile_type', profileType);
    localStorage.setItem('wassla_profile_type_set_at', new Date().toISOString());

    // Optimistic UI update — keep role / account_type / profile_type in sync
    setUser(currentUser => currentUser ? {
      ...currentUser,
      role: profileType,
      account_type: profileType,
      profile_type: profileType,
      needs_profile_type: false,
      onboarding_completed: true,
      onboardingCompleted: true,
      onboarding_version_completed: ONBOARDING_DONE,
    } : currentUser);
    setNeedsProfileType(false);
    setShowOnboarding(false);

    // Persist to both backend endpoints in parallel.
    const baseUrl = process.env.REACT_APP_BACKEND_URL;
    const authHeaders = {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${currentToken}`,
    };
    try {
      const [ptRes, roleRes] = await Promise.allSettled([
        fetch(`${baseUrl}/api/me/profile-type`, {
          method: 'POST',
          headers: authHeaders,
          body: JSON.stringify({ profile_type: profileType }),
        }),
        fetch(`${baseUrl}/api/auth/role`, {
          method: 'PATCH',
          headers: authHeaders,
          body: JSON.stringify({ role: profileType }),
        }),
      ]);
      console.log(
        '[Auth] profile-type persist:',
        ptRes.status === 'fulfilled' ? ptRes.value.status : ptRes.reason,
        '| role persist:',
        roleRes.status === 'fulfilled' ? roleRes.value.status : roleRes.reason,
      );
    } catch (error) {
      console.error('[Auth] Error persisting profile type:', error);
      // Keep optimistic UI — user already made their choice.
    }
    return true;
  }, []);

  // Dismiss profile type picker (user clicked "Later")
  const dismissProfileTypePicker = useCallback(async () => {
    console.log('[Auth] Dismissing profile type picker');
    const currentToken = localStorage.getItem('token');
    if (!currentToken) {
      // Not logged in, just dismiss locally
      setNeedsProfileType(false);
      return;
    }
    
    try {
      await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/me/profile-type-dismiss`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${currentToken}`
        }
      });
    } catch (error) {
      console.error('[Auth] Error dismissing profile type picker:', error);
    }
    
    // Local sync with the server flag so no gate re-triggers this session.
    setUser(prev => prev ? { ...prev, onboarding_completed: true, onboardingCompleted: true } : prev);
    setNeedsProfileType(false);
  }, []);

  return (
    <AuthContext.Provider value={{ 
      user, 
      token, 
      loading,
      authError,
      retryAuth,
      isReady,
      isLoggedIn: !!user,
      login, 
      logout, 
      setUser,
      updateUser,
      refreshUser,
      showOnboarding,
      setShowOnboarding,
      showCoinsPopup,
      setShowCoinsPopup,
      updateCoins,
      setRole,
      // New profile type methods
      needsProfileType,
      setNeedsProfileType,
      setProfileType,
      dismissProfileTypePicker,
      beginGoogleOAuth,
      oauthInProgress,
      completeOAuthTransition,
    }}>
      {oauthInProgress && (
        <div
          data-testid="google-signin-overlay"
          style={{
            position: 'fixed',
            inset: 0,
            zIndex: 100000,
            background: '#ffffff',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 16,
          }}
        >
          <div
            style={{
              width: 44,
              height: 44,
              border: '4px solid rgba(31,111,58,0.18)',
              borderTopColor: '#1F6F3A',
              borderRadius: '50%',
              animation: 'wassla-oauth-spin 0.9s linear infinite',
            }}
          />
          <div style={{ fontSize: 16, fontWeight: 700, color: '#134725', textAlign: 'center', padding: '0 24px' }}>
            {document.documentElement.dir === 'rtl' ? 'جاري تسجيل دخولك…' : 'Signing you in…'}
          </div>
          <button
            type="button"
            data-testid="google-signin-cancel"
            onClick={clearOAuthState}
            style={{
              marginTop: 8,
              background: 'none',
              border: 'none',
              color: '#6b7280',
              fontSize: 14,
              textDecoration: 'underline',
              cursor: 'pointer',
            }}
          >
            {document.documentElement.dir === 'rtl' ? 'إلغاء' : 'Cancel'}
          </button>
          <style>{'@keyframes wassla-oauth-spin{to{transform:rotate(360deg)}}'}</style>
        </div>
      )}
      {children}
    </AuthContext.Provider>
  );
};
